import { motion } from "framer-motion";
import { Link } from "react-router-dom";

/**
 * Higgsfield-style hero: thin editorial serif, word-by-word blur reveal,
 * warm champagne accent, generous whitespace, film-grain overlay.
 */
export const CinematicHero = ({
  eyebrow = "Indialantic, Florida",
  headline,
  italic,
  body,
  image,
  primaryCta = { label: "Request a Tour", to: "/contact" },
}) => {
  const words = headline.split(" ");
  const italicWords = italic ? italic.split(" ") : [];

  return (
    <section
      className="relative h-screen w-full overflow-hidden cinematic-grain cinematic-vignette bg-black"
      data-testid="cinematic-hero"
    >
      <motion.img
        src={image}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        initial={{ scale: 1.18 }}
        animate={{ scale: 1.02 }}
        transition={{ duration: 24, ease: "linear" }}
        fetchPriority="high"
      />
      <div className="absolute inset-x-0 top-0 h-64 bg-gradient-to-b from-black/80 via-black/30 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-[65vh] bg-gradient-to-t from-black via-black/50 to-transparent" />

      <div className="absolute inset-0 flex items-end">
        <div className="container-premium pb-20 md:pb-32">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.4 }}
            className="chapter-label mb-8"
          >
            {eyebrow}
          </motion.p>

          <h1 className="cinematic-serif text-[#F5F1EA] text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-[110px] leading-[0.98] max-w-6xl">
            {words.map((w, i) => (
              <motion.span
                key={i}
                initial={{ opacity: 0, y: 60, filter: "blur(14px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                transition={{ duration: 1.1, delay: 0.5 + i * 0.09, ease: [0.2, 0.65, 0.3, 0.9] }}
                className="inline-block mr-[0.22em]"
              >
                {w}
              </motion.span>
            ))}
            {italic && (
              <>
                <br />
                {italicWords.map((w, i) => (
                  <motion.span
                    key={`i-${i}`}
                    initial={{ opacity: 0, y: 60, filter: "blur(14px)" }}
                    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                    transition={{
                      duration: 1.1,
                      delay: 0.5 + (words.length + i) * 0.09,
                      ease: [0.2, 0.65, 0.3, 0.9],
                    }}
                    className="cinematic-serif-italic inline-block mr-[0.22em]"
                  >
                    {w}
                  </motion.span>
                ))}
              </>
            )}
          </h1>

          {body && (
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 1.9 }}
              className="cinematic-body mt-8 max-w-md text-[15px] md:text-base"
            >
              {body}
            </motion.p>
          )}

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 2.2 }}
            className="mt-10"
          >
            <Link
              to={primaryCta.to}
              data-testid="hero-cta-btn"
              className="inline-flex items-center gap-3 border border-[#C4A570] text-[#C4A570] hover:bg-[#C4A570] hover:text-[#0D0D0D] transition-colors duration-500 px-8 py-3.5 rounded-full text-[11px] uppercase tracking-[0.35em]"
            >
              {primaryCta.label}
              <span className="w-3 h-px bg-current" />
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3"
      >
        <span className="text-[9px] uppercase tracking-[0.5em] text-white/50">Scroll</span>
        <motion.div
          animate={{ scaleY: [0.2, 1, 0.2], originY: 0 }}
          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
          className="h-8 w-px bg-[#C4A570]"
        />
      </motion.div>
    </section>
  );
};

export default CinematicHero;
